/**
 * Material-UI styles hook for the Levels component.
 *
 * @module components/sim/Levels
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import {makeStyles} from '@material-ui/core/styles';

/**
 * A hook that generates dynamic styles for a component using Material-UI's theming capabilities.
 *
 * This object consists of multiple style definitions, each representing a specific component or element.
 * Various styles are responsive, leveraging Material-UI's breakpoints for different screen sizes.
 * The styles also use theme-based spacing, colors, and shadows for consistent design.
 *
 * Styles:
 * - `root`: Base styles for the main container.
 * - `flexItem1`: Styles for the first flex item, includes responsive width and padding.
 * - `flexItem2`: Styles for the second flex item, defining its width.
 * - `flexItem3`: Styles for the third flex item, centering content and adding padding.
 * - `status`: Styles for a status container, aligning content at the end with spacing.
 * - `startBtn`: Styling for the start button with bold fonts and padding.
 * - `practiceBtn`: Styling for the practice button with custom background color and bold text.
 * - `autoplayBtn`: Styling for the autoplay button with custom background color and bold text.
 * - `textBold`: Style for bold text.
 * - `radio`: Custom styling for a radio button with colors and dimensions.
 * - `flex2item1`: Styles for an element aligned to flex-start.
 * - `popper`: Styles for a popper element with high z-index and spacing.
 * - `popperPaper`: Styles for the content within a popper, defining layout and spacing.
 * - `rightTriangle`: A triangle-shaped element used for adding design specifics like arrow pointers.
 * - `paper`: Styles for a modal or similar component with shadows and padding.
 * - `checkBoxFilled`: Custom style for a filled checkbox with specific color.
 * - `checkBox`: Custom styling for checkboxes with adjusted icon size.
 * - `levelDiv`: Styles for a specific div element with left margin.
 *
 * The styles are designed to encapsulate consistency and flexibility across components by utilizing the Material-UI theme system.
 */
export const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#f1f1f1',
        [theme.breakpoints.down('lg')]: {
            fontSize: '0.9em',
        },
    },
    flexItem1: {
        width: '40%',
        borderRight: '1px solid lightgrey',
        [theme.breakpoints.down('lg')]: {
            width: '40%',
        },
        padding: theme.spacing(2),
    },
    flexItem2: {
        width: '35%',
    },
    flexItem3: {
        alignSelf: 'center',
        justifySelf: 'center',
        padding: theme.spacing(0, 2),
    },
    status: {
        display: 'flex',
        height: '100%',
        alignItems: 'flex-end',
        padding: theme.spacing(2),
    },
    startBtn: {
        fontSize: '0.8em',
        alignSelf: 'center',
        padding: theme.spacing(1, 4, 1, 4),
        fontWeight: 'bold',
    },
    practiceBtn: {
        fontSize: '0.8em',
        backgroundColor: '#eedee3',
        '& .MuiButton-label': {
            color: '#882647',
            fontWeight: 'bold',
        },
    },
    autoplayBtn: {
        fontSize: '0.8em',
        backgroundColor: '#dde7fa',
        '& .MuiButton-label': {
            color: '#264172',
            fontWeight: 'bold',
        },
    },
    textBold: {
        fontWeight: 'bold',
    },
    radio: {
        color: theme.palette.grey[300],
        backgroundColor: 'white',
        borderRadius: '50%',
        width: '10px',
        height: '10px',
        marginRight: '10px',
    },
    flex2item1: {
        alignSelf: 'flex-start',
    },

    popper: {
        zIndex: 9000,
        marginTop: theme.spacing(5),
        marginRight: theme.spacing(0.3),
    },
    popperPaper: {
        padding: theme.spacing(2),
        gap: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
    },
    rightTriangle: {
        width: '`10px',
        height: '10px',
        borderTop: '10px solid transparent',
        borderBottom: '10px solid transparent',
        borderLeft: '10px solid white',
        position: 'absolute',
        top: '25px',
        right: '-10px',
        zIndex: '1000',
    },
    paper: {
        position: 'absolute',
        top: '40%',
        right: '120px',
        width: 400,
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    checkBoxFilled: {
        color: '#5bbb5f !important',
    },
    checkBox: {
        '& .MuiSvgIcon-root': {
            width: 20,
        },
    },
    levelDiv: {
        marginLeft: 38,
    },
}));
